import * as React from 'react'
import { createPortal } from 'react-dom'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'

import { Button } from '~/atoms/button/Button'
import { Toast } from '~/atoms/popup/Toast'
import { ModalWrapper } from '~/atoms/modal/ModalWrapper'
import { SupportEventStart } from '~/organisms/reserve/SupportEventStart'
import { SupportEventTakeCare } from '~/organisms/reserve/SupportEventTakeCare'
import { SupportEventEating } from '~/organisms/reserve/SupportEventEating'
import { SupportEventExcretion } from '~/organisms/reserve/SupportEventExcretion'
import { SupportEventSleep } from '~/organisms/reserve/SupportEventSleep'
import { SupportEventBathing } from '~/organisms/reserve/SupportEventBathing'
import { SupportEventPlaying } from '~/organisms/reserve/SupportEventPlaying'
import { SupportEventGoingOut } from '~/organisms/reserve/SupportEventGoingOut'
import { SupportEventOther } from '~/organisms/reserve/SupportEventOther'
import { SupportEventTemperature } from '~/organisms/reserve/SupportEventTemperature'
import { SupportEventHandover } from '~/organisms/reserve/SupportEventHandover'
import { SupportEventFinish } from '~/organisms/reserve/SupportEventFinish'
import { SupportEventModal } from '~/organisms/reserve/SupportEventModal'
import AddIcon from './add.svg?reactCompornt'

import * as Style from './style.css'

export interface IProps {
  isInitialized?: boolean
  control: Control<IReserveSupportMemo>
  fields: FieldArrayWithId<
    ICompleteReportFormStateStep2,
    'supportEvents',
    'id'
  >[]
  errors: FieldErrors<IReserveSupportMemo>
  errorMessages?: string[]
  supportChildren: IChildSummary[]
  inputSupportEvents: ISupportEvent[]
  minDate: Date
  showToast?: boolean
  showErrorToast?: boolean
  showConfirm?: boolean
  showDeleteConfirm?: boolean
  showSupportEventModal?: boolean
  register: UseFormRegister<IReserveSupportMemo>
  setValue: UseFormSetValue<IReserveSupportMemo>
  submitHandler: () => void
  cancelHandler: () => void
  openSupportEventHandler: () => void
  closeSupportEventHandler: () => void
  addSupportEventHandler: (supportEventType: ISupportEventType) => void
  deleteSupportEventHandler: (index: number) => void
  confirmApproveHandler: () => void
  confirmCancelHandler: () => void
  deleteApproveHandler: () => void
  deleteCancelHandler: () => void
}

export const SupportMemo = ({
  isInitialized,
  control,
  fields,
  errors,
  errorMessages,
  supportChildren,
  inputSupportEvents,
  minDate,
  showToast,
  showErrorToast,
  showConfirm,
  showDeleteConfirm,
  showSupportEventModal,
  register,
  setValue,
  submitHandler,
  cancelHandler,
  openSupportEventHandler,
  closeSupportEventHandler,
  addSupportEventHandler,
  deleteSupportEventHandler,
  confirmApproveHandler,
  confirmCancelHandler,
  deleteApproveHandler,
  deleteCancelHandler,
}: IProps) => {
  const supportEventFinish = inputSupportEvents.find(
    (supportEvent) => supportEvent.eventType === 'Finish',
  )

  return (
    <div className={Style.SupportMemo}>
      <h1 className={Style.SupportMemoTitle}>サポートメモ</h1>
      <p className={Style.SupportMemoDescription}>
        「サポートメモ」は、サポート中に保存した保育内容の記録を完了報告に反映できる機能です。入力後は保存しましょう。保育の妨げにならないよう、記録は簡潔かつ迅速に行いましょう。
      </p>
      <form>
        <div className={Style.SupportMemoSupportEvent}>
          {fields?.map((field, index) => (
            <div key={field.id} className={Style.SupportMemoSupportEventItem}>
              {field.eventType === 'Start' && (
                <SupportEventStart
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={inputSupportEvents[index] as ISupportEventStart}
                  supportChildren={supportChildren}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                />
              )}
              {field.eventType === 'TakeCare' && (
                <SupportEventTakeCare
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventTakeCare
                  }
                  supportChildren={supportChildren}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Eating' && (
                <SupportEventEating
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventEating
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Excretion' && (
                <SupportEventExcretion
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventExcretion
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Sleep' && (
                <SupportEventSleep
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={inputSupportEvents[index] as ISupportEventSleep}
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Bathing' && (
                <SupportEventBathing
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventBathing
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Playing' && (
                <SupportEventPlaying
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventPlaying
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'GoingOut' && (
                <SupportEventGoingOut
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventGoingOut
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Other' && (
                <SupportEventOther
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={inputSupportEvents[index] as ISupportEventOther}
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Temperature' && (
                <SupportEventTemperature
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventTemperature
                  }
                  errors={errors}
                  register={register}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Handover' && (
                <SupportEventHandover
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventHandover
                  }
                  supportChildren={supportChildren}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
              {field.eventType === 'Finish' && (
                <SupportEventFinish
                  control={control}
                  index={index}
                  minDate={minDate}
                  validation={false}
                  animation={isInitialized}
                  supportEvent={
                    inputSupportEvents[index] as ISupportEventFinish
                  }
                  supportChildren={supportChildren}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  deleteHandler={deleteSupportEventHandler}
                />
              )}
            </div>
          ))}
        </div>
        <div className={Style.SupportMemoAdd}>
          <button
            type="button"
            className={Style.SupportMemoAddButton}
            onClick={openSupportEventHandler}
          >
            <AddIcon className={Style.SupportMemoAddIcon} />
            <div>
              <p className={Style.SupportMemoAddButtonText}>
                保育内容を追加する
              </p>
              <p className={Style.SupportMemoAddButtonTextSub}>
                終了報告は必ず追加してください
              </p>
            </div>
          </button>
        </div>
        <ul className={Style.SupportMemoActions}>
          <li className={Style.SupportMemoActionSecondary}>
            <Button type="button" color="secondary" onClick={cancelHandler}>
              戻る
            </Button>
          </li>
          <li className={Style.SupportMemoActionPrimary}>
            <Button type="button" color="primary" onClick={submitHandler}>
              保存
            </Button>
          </li>
        </ul>
      </form>
      {isInitialized &&
        createPortal(
          <>
            <Toast
              visible={showToast}
              title="一時保存しました"
              color="notice"
            ></Toast>
            <Toast
              visible={showErrorToast}
              title="保存できませんでした"
              color="alert"
            >
              {errorMessages?.map((message, index) => (
                <p key={`error-message-${index}`}>・{message}</p>
              ))}
            </Toast>
            <ModalWrapper
              mobileAnimation="slideUp"
              isOpen={showSupportEventModal}
            >
              <div className={Style.SupportEventModal}>
                <SupportEventModal
                  disabledFinish={!!supportEventFinish}
                  clickHandler={addSupportEventHandler}
                  closeHanlder={closeSupportEventHandler}
                />
              </div>
            </ModalWrapper>

            <ModalWrapper
              animation="fadeIn"
              mobileAnimation="fadeIn"
              isOpen={showConfirm}
            >
              <div className={Style.SupportMemoConfirm}>
                <div className={Style.SupportMemoConfirmContent}>
                  <p className={Style.SupportMemoConfirmMessage}>
                    変更を破棄しますか？
                  </p>
                  <ul className={Style.SupportMemoConfirmActions}>
                    <li>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={confirmCancelHandler}
                      >
                        キャンセル
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        color="primary"
                        onClick={confirmApproveHandler}
                      >
                        破棄する
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </ModalWrapper>
            <ModalWrapper
              animation="fadeIn"
              mobileAnimation="fadeIn"
              isOpen={showDeleteConfirm}
            >
              <div className={Style.SupportMemoConfirm}>
                <div className={Style.SupportMemoConfirmContent}>
                  <p className={Style.SupportMemoConfirmMessage}>
                    本当に削除しますか？
                  </p>
                  <ul className={Style.SupportMemoConfirmActions}>
                    <li>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={deleteCancelHandler}
                      >
                        キャンセル
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        color="primary"
                        onClick={deleteApproveHandler}
                      >
                        削除する
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </ModalWrapper>
          </>,
          document.body,
        )}
    </div>
  )
}
